/**
 * ComposeMessageDialog Component
 * Main dialog for composing and sending inquiries to experts
 * Supports both authenticated users and guests with reCAPTCHA protection
 */

import { useState, useEffect, useRef, useDeferredValue, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/supabaseQuery';
import { INTERNATIONAL_LOCATION_ID } from '@/constants/config';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useUserContext } from '@/contexts/UserContext';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LCommon, LComponents } from '@/components/i18n';
import { useAvatar } from '@/contexts/AvatarContext';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { cn } from '@/lib/utils';
import { getSmartAuthRoute } from '@/utils/authRedirect';
import { LegalDocumentModal } from '@/components/LegalDocumentModal';

import { SPACING, DIALOGS, GRIDS } from '@/design-system';
import { RESPONSIVE } from '@/design-system/helpers/responsive';
import { DIALOG_TYPOGRAPHY } from '@/design-system/patterns/dialogs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Loader2, Send, Cloud } from 'lucide-react';

import { ComposeSenderHeader } from './ComposeSenderHeader';
import { ComposeSenderInput } from './ComposeSenderInput';
import { ComposeRecipientHeader } from './ComposeRecipientHeader';
import { ComposeRecipientSection } from './ComposeRecipientSection';
import { ComposeMessageForm } from './ComposeMessageForm';
import { ComposeOptionalDetails } from './ComposeOptionalDetails';
import { ComposeTargetLocation } from './ComposeTargetLocation';
import { ComposeSuccessView } from './ComposeSuccessView';
import { ComposeMessageHistory, SentMessage } from './ComposeMessageHistory';
import { useComposeForm } from './hooks/useComposeForm';
import { useComposeDraft } from './hooks/useComposeDraft';
import { useMessageDrafts, MessageDraft } from '@/hooks/useMessageDrafts';
import type { Expert, ComposeMessageDialogProps, UserData, InitialRecipient } from './types';

export function ComposeMessageDialog(props: ComposeMessageDialogProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  const open = props.open !== undefined ? props.open : internalOpen;
  const onOpenChange = props.onOpenChange || setInternalOpen;
  const onSuccess = props.onSuccess || (() => {});
  const { initialRecipients, initialSubject, initialMessage, initialServiceType, initialLocation, lockedRecipients, context, hideTrigger } = props;
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lCommon, lComponents } = useLocalizations();
  const { currentLocation: location } = useUserContext();
  const { avatarUrl, displayName: senderDisplayName } = useAvatar();
  
  // Guest detection
  const isGuest = !user;
  
  // reCAPTCHA integration for bot protection
  const { executeRecaptcha, verifyToken, isConfigured: recaptchaConfigured } = useRecaptcha(open);
  
  // Form and draft hooks - extended for guest support
  const { 
    formData, 
    errors, 
    fieldStatus, 
    guestEmail,
    guestName,
    setFormData,
    updateFormData, 
    handleFieldBlur, 
    validateForm, 
    clearRecipientError, 
    resetForm: resetFormState,
    setErrors,
    setGuestEmail,
    setGuestName,
  } = useComposeForm();
  
  // Initialize selectedExperts directly from props to avoid timing issues
  const [selectedExperts, setSelectedExperts] = useState<Expert[]>(() => {
    if (initialRecipients && initialRecipients.length > 0) {
      return initialRecipients.map((r: InitialRecipient) => ({
        id: r.id,
        display_name: r.name,
        name: r.name,
        lastname: null,
        business: null,
        city: null,
        state_province: null,
        country: null,
        image: r.image || null,
        membership_tier: null,
        profile_listed: null,
        about: null,
        vanity_url: null,
      })) as Expert[];
    }
    return [];
  });
  const [isSending, setIsSending] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const hasTrackedOpen = useRef(false);
  
  // Post-send success state
  const [submissionState, setSubmissionState] = useState<'form' | 'success'>('form');
  const [sentExperts, setSentExperts] = useState<Expert[]>([]);
  const [similarExperts, setSimilarExperts] = useState<Expert[]>([]);
  const [selectedSimilarExperts, setSelectedSimilarExperts] = useState<string[]>([]);
  const [isSendingBulk, setIsSendingBulk] = useState(false);
  const [isLoadingSimilarExperts, setIsLoadingSimilarExperts] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  
  // Draft management
  const { currentDraftId, lastSavedAt, isDraftSaving, resetDraft } = useComposeDraft({
    formData,
    selectedExperts,
    isOpen: open,
  });
  
  // Message drafts for history
  const { drafts, isLoading: draftsLoading, deleteDraft } = useMessageDrafts();

  // Track compose_open event when dialog opens
  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      const trackComposeOpen = async () => {
        try {
          await supabase.functions.invoke('track-analytics-event', {
            body: {
              eventType: 'compose_open',
              eventCategory: 'engagement',
              sourcePage: window.location.pathname,
              metadata: {
                hasPreselectedExperts: selectedExperts.length > 0,
                timestamp: new Date().toISOString()
              }
            }
          });
        } catch {
          // Silent fail
        }
      };
      trackComposeOpen();
    }
    
    if (!open) {
      hasTrackedOpen.current = false;
    }
  }, [open, selectedExperts.length]);

  // Fetch user's email and name
  const { data: userData } = useQuery({
    queryKey: ['user-data', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('email, display_name, name, location_id, state_province, city')
        .eq('id', user?.id)
        .maybeSingle();

      if (error) throw error;
      return data as UserData | null;
    },
    enabled: !!user?.id && open
  });

  // Fetch recent contacts
  const { data: recentContacts } = useQuery({
    queryKey: ['recent-contacts', user?.id],
    queryFn: async () => {
      if (!userData?.email) return [];
      
      const { data: recentOpps } = await supabase
        .from('opportunities')
        .select('recipient_id')
        .eq('sender_email', userData.email)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (!recentOpps?.length) return [];
      
      const recipientIds = [...new Set(recentOpps.map(o => o.recipient_id))].slice(0, 5);
      
      const { data: experts } = await supabase
        .from('users')
        .select('id, display_name, name, lastname, business, city, state_province, country, image, membership_tier, profile_listed, about, vanity_url')
        .in('id', recipientIds)
        .eq('profile_active', true);
      
      return (experts || []) as Expert[];
    },
    enabled: open && !!userData?.email && context !== 'tax-oracle'
  });

  // Location expert suggestions with fallback
  const hasSelectedLocation = !!(formData.location && formData.location.trim());
  const isInternationalSelected = formData.location === INTERNATIONAL_LOCATION_ID;
  const nearYouLocationId = userData?.location_id;
  
  // Query for selected location experts (skip if international)
  const { data: selectedLocationMatches } = useQuery({
    queryKey: ['selected-location-matches', formData.location],
    queryFn: async () => {
      if (!formData.location || isInternationalSelected) return [];
      
      const { data: experts } = await supabase
        .from('users')
        .select('id, display_name, name, lastname, business, city, state_province, country, image, membership_tier, profile_listed, about, vanity_url')
        .eq('location_id', formData.location)
        .eq('profile_listed', true)
        .eq('profile_active', true)
        .neq('id', user?.id)
        .order('global_rank', { ascending: true, nullsFirst: false })
        .limit(5);
      
      return (experts || []) as Expert[];
    },
    enabled: open && hasSelectedLocation && !isInternationalSelected && context !== 'tax-oracle'
  });
  
  // Query for "near you" experts (user's location - fallback)
  const { data: nearYouMatches } = useQuery({
    queryKey: ['near-you-matches', nearYouLocationId],
    queryFn: async () => {
      if (!nearYouLocationId) return [];
      
      const { data: experts } = await supabase
        .from('users')
        .select('id, display_name, name, lastname, business, city, state_province, country, image, membership_tier, profile_listed, about, vanity_url')
        .eq('location_id', nearYouLocationId)
        .eq('profile_listed', true)
        .eq('profile_active', true)
        .neq('id', user?.id)
        .order('global_rank', { ascending: true, nullsFirst: false })
        .limit(5);
      
      return (experts || []) as Expert[];
    },
    enabled: open && !!nearYouLocationId && context !== 'tax-oracle'
  });
  
  // Decide which matches to show and label mode
  const locationMatches = isInternationalSelected 
    ? nearYouMatches 
    : (selectedLocationMatches?.length ? selectedLocationMatches : nearYouMatches);
  
  const locationMatchesMode: 'top_experts' | 'near_you' = 
    (hasSelectedLocation && !isInternationalSelected && selectedLocationMatches?.length) 
      ? 'top_experts' 
      : 'near_you';

  // Search experts
  const { data: experts, isLoading: expertsLoading, refetch: refetchExperts } = useQuery({
    queryKey: ['experts-search', deferredSearchQuery, locationFilter],
    queryFn: async () => {
      let query = supabase
        .from('users')
        .select(`
          id, 
          display_name, 
          name, 
          lastname, 
          business, 
          city, 
          state_province, 
          country, 
          image, 
          membership_tier, 
          profile_listed,
          about,
          vanity_url,
          users_users_practice_areas!fk_uupa_user(
            users_practice_areas(
              practice_area
            )
          )
        `)
        .eq('profile_listed', true)
        .eq('profile_active', true)
        .neq('id', user?.id);

      if (locationFilter && locationFilter !== 'all') {
        query = query.eq('location_id', locationFilter);
      }

      if (deferredSearchQuery.trim()) {
        query = query.or(`name.ilike.%${deferredSearchQuery}%,display_name.ilike.%${deferredSearchQuery}%,business.ilike.%${deferredSearchQuery}%,city.ilike.%${deferredSearchQuery}%,lastname.ilike.%${deferredSearchQuery}%`);
      }

      const { data, error } = await query
        .order('display_name')
        .limit(deferredSearchQuery.trim() ? 50 : 20);

      if (error) throw error;
      return (data || []) as unknown as Expert[];
    },
    enabled: open && !!user?.id
  });

  // Initialize location filter from UserContext on dialog open
  useEffect(() => {
    if (open && user?.id) {
      setLocationFilter(location || 'all');
      setSearchQuery('');
      refetchExperts();
    }
  }, [open, user?.id, location, refetchExperts]);

  // Load initial recipients when dialog opens
  useEffect(() => {
    if (open && initialRecipients && initialRecipients.length > 0) {
      const expertsToLoad = initialRecipients.map((r: InitialRecipient) => ({
        id: r.id,
        display_name: r.name,
        name: r.name,
        lastname: null,
        business: null,
        city: null,
        state_province: null,
        country: null,
        image: r.image || null,
        membership_tier: null,
        profile_listed: null,
        about: null,
        vanity_url: null,
      })) as Expert[];
      setSelectedExperts(expertsToLoad);
    }
  }, [open, initialRecipients]);

  // Load initial form data when dialog opens - pre-load detected location
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        subject: initialSubject || prev.subject,
        message: initialMessage || prev.message,
        service_type: initialServiceType || prev.service_type,
        // location from UserContext is already a location_id
        location: initialLocation || location || prev.location,
      }));
    }
  }, [open, initialSubject, initialMessage, initialServiceType, initialLocation, location, setFormData]);

  const handleAddExpert = useCallback((expert: Expert) => {
    if (selectedExperts.length >= 3) {
      toast.error(lCommon('toast.max_recipients.error', { count: 3, defaultValue: 'Maximum {{count}} recipients allowed.' }));
      return;
    }
    
    if (selectedExperts.find(e => e.id === expert.id)) {
      toast.error(lCommon('toast.expert_already_selected.error', { defaultValue: 'The expert is already selected.' }));
      return;
    }

    setSelectedExperts(prev => [...prev, expert]);
    clearRecipientError();
    
    const { getExpertDisplayName } = require('./types');
    const expertName = getExpertDisplayName(expert) || lCommon('labels.expert', { defaultValue: 'Expert' });
    toast.success(lCommon('toast.expert_added.success', { name: expertName, defaultValue: '{{name}} added successfully.' }));
  }, [selectedExperts, lCommon, clearRecipientError]);

  const handleRemoveExpert = useCallback((expertId: string) => {
    if (lockedRecipients) {
      toast.error(lCommon('compose_message.error.recipients_locked', { defaultValue: 'Recipients cannot be changed.' }));
      return;
    }
    setSelectedExperts(prev => prev.filter(e => e.id !== expertId));
  }, [lockedRecipients, lCommon]);

  const resetForm = useCallback(() => {
    setSelectedExperts([]);
    resetFormState();
    resetDraft();
    setSubmissionState('form');
    setSentExperts([]);
    setSimilarExperts([]);
    setSelectedSimilarExperts([]);
  }, [resetFormState, resetDraft]);

  // Handle loading a draft
  const handleLoadDraft = useCallback((draft: MessageDraft) => {
    // Load recipients from draft
    if (draft.recipient_ids?.length > 0) {
      // Fetch recipient details
      supabase
        .from('users')
        .select('id, display_name, name, lastname, business, city, state_province, country, image, membership_tier, profile_listed, about, vanity_url')
        .in('id', draft.recipient_ids)
        .then(({ data }) => {
          if (data?.length) {
            setSelectedExperts(data as Expert[]);
          }
        });
    }
    
    // Load form data
    setFormData({
      subject: draft.subject || '',
      message: draft.message || '',
      service_type: draft.service_type || '',
      location: draft.location || '',
      budget_range: draft.budget_range || '',
      timeline: draft.timeline || '',
    });
    
    toast.success(lCommon('toast.draft_loaded.success', { defaultValue: 'Draft loaded successfully.' }));
  }, [setFormData, lCommon]);

  // Handle loading a sent message for follow-up
  const handleLoadSent = useCallback((sentMessage: SentMessage) => {
    // Pre-fill subject as "Re: Original Subject" for follow-up
    const followUpSubject = sentMessage.subject 
      ? `Re: ${sentMessage.subject.replace(/^Re:\s*/i, '')}`
      : '';
    
    setFormData(prev => ({
      ...prev,
      subject: followUpSubject,
      message: '', // Clear message for fresh follow-up
    }));
    
    toast.info(lCommon('toast.loaded_for_followup.info', { defaultValue: 'Message loaded for follow-up.' }));
  }, [setFormData, lCommon]);

  // Handle deleting a draft
  const handleDeleteDraft = useCallback((draftId: string) => {
    deleteDraft(draftId);
  }, [deleteDraft]);

  // Fetch similar experts after sending using intelligent matching
  const fetchSimilarExperts = async (sentExpertIds: string[], locationId?: string | null) => {
    setIsLoadingSimilarExperts(true);
    try {
      // Extract topics from form data for intelligent matching
      const inferredTopics = formData.service_type ? [formData.service_type] : [];
      
      // Call edge function for intelligent matching
      const { data, error } = await supabase.functions.invoke('match-experts', {
        body: { 
          topics: inferredTopics,
          location: locationId,
          excludeIds: [...sentExpertIds, user?.id].filter(Boolean),
          limit: 3
        }
      });
      
      if (error) throw error;
      
      // Map response to Expert type with match data
      const matches = (data?.matches || []).map((match: {
        id: string;
        full_name?: string;
        profile_image_url?: string;
        firm_name?: string;
        score?: number;
        reasoning?: string;
      }) => ({
        id: match.id,
        display_name: match.full_name || 'Expert',
        name: match.full_name || 'Expert',
        lastname: null,
        business: match.firm_name || null,
        city: null,
        state_province: null,
        country: null,
        image: match.profile_image_url || null,
        membership_tier: 'expert',
        profile_listed: true,
        about: match.reasoning || null,
        vanity_url: null,
        matchScore: match.score,
        matchReason: match.reasoning,
      })) as Expert[];
      
      setSimilarExperts(matches);
    } catch {
      // Fallback to simple query if edge function fails
      try {
        let query = supabase
          .from('users')
          .select('id, display_name, name, lastname, business, city, state_province, country, image, membership_tier, profile_listed, about, vanity_url')
          .eq('profile_listed', true)
          .eq('profile_active', true)
          .not('id', 'in', `(${sentExpertIds.join(',')})`)
          .neq('id', user?.id);
        
        if (locationId) {
          query = query.eq('location_id', locationId);
        }
        
        const { data } = await query
          .order('global_rank', { ascending: true, nullsFirst: false })
          .limit(3);
        
        setSimilarExperts((data || []) as Expert[]);
      } catch {
        // Silent fail
      }
    } finally {
      setIsLoadingSimilarExperts(false);
    }
  };

  const handleToggleSimilarExpert = useCallback((expertId: string) => {
    setSelectedSimilarExperts(prev =>
      prev.includes(expertId) 
        ? prev.filter(id => id !== expertId)
        : [...prev, expertId]
    );
  }, []);

  const handleSendToSimilar = async () => {
    // Determine sender info based on guest/logged-in status
    const senderEmail = isGuest ? guestEmail.trim() : userData?.email;
    const senderName = isGuest ? (guestName.trim() || null) : (userData?.display_name || userData?.name);
    
    if (selectedSimilarExperts.length === 0 || !senderEmail) return;
    
    // reCAPTCHA verification for guests
    if (isGuest && recaptchaConfigured) {
      try {
        const token = await executeRecaptcha('guest_compose_similar');
        if (token) {
          const result = await verifyToken(token, 'guest_compose_similar');
          if (!result.success || (result.score && result.score < 0.5)) {
            toast.error(lComponents('compose_message.error.verification_failed', { defaultValue: 'Verification failed. Please try again.' }));
            return;
          }
        }
      } catch {
        toast.error(lComponents('compose_message.error.verification_failed', { defaultValue: 'Verification failed. Please try again.' }));
        return;
      }
    }
    
    setIsSendingBulk(true);
    try {
      const expertsToSend = similarExperts.filter(e => selectedSimilarExperts.includes(e.id));
      
      const submissions = expertsToSend.map(expert => ({
        recipient_id: expert.id,
        sender_email: senderEmail,
        sender_name: senderName,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        service_type: formData.service_type || null,
        location: formData.location?.trim() || null,
        budget_range: formData.budget_range || null,
        timeline: formData.timeline || null,
        user_status: 'seeking_more',
        status: 'released_to_provider',
        source_type: 'form',
        email_verified: !isGuest, // Guest submissions are unverified
      }));

      const { error } = await supabase
        .from('opportunities')
        .insert(submissions);

      if (error) throw error;

      toast.success(lCommon('toast.message_sent.success', { count: expertsToSend.length, defaultValue: 'Message sent to {{count}} expert(s).' }), { id: 'send-message-similar' });

      // Invalidate outbox count queries
      queryClient.invalidateQueries({ queryKey: ['outbox-count'] });
      queryClient.invalidateQueries({ queryKey: ['user-opportunities'] });

      setSimilarExperts(prev => prev.filter(e => !selectedSimilarExperts.includes(e.id)));
      setSentExperts(prev => [...prev, ...expertsToSend]);
      setSelectedSimilarExperts([]);
    } catch {
      toast.error(lCommon('toast.failed_to_send.message', { defaultValue: 'Failed to send message.' }), { id: 'send-message-similar' });
    } finally {
      setIsSendingBulk(false);
    }
  };

  const handleCloseSuccess = useCallback(() => {
    resetForm();
    onSuccess();
    onOpenChange(false);
  }, [resetForm, onSuccess, onOpenChange]);

  const handleSend = async () => {
    // Determine sender info based on guest/logged-in status
    const senderEmail = isGuest ? guestEmail.trim() : userData?.email;
    const senderName = isGuest ? (guestName.trim() || null) : (userData?.display_name || userData?.name);
    
    // Validate form (pass isGuest flag for guest field validation)
    if (!validateForm(selectedExperts, isGuest)) return;
    
    // Check that we have a valid sender email
    if (!senderEmail) {
      toast.error(lComponents('compose_message.error.email_required', { defaultValue: 'Email is required.' }));
      return;
    }
    
    // reCAPTCHA verification for guests
    if (isGuest && recaptchaConfigured) {
      try {
        const token = await executeRecaptcha('guest_compose');
        if (token) {
          const result = await verifyToken(token, 'guest_compose');
          if (!result.success || (result.score && result.score < 0.5)) {
            toast.error(lComponents('compose_message.error.verification_failed', { defaultValue: 'Verification failed. Please try again.' }));
            return;
          }
        }
      } catch {
        toast.error(lComponents('compose_message.error.verification_failed', { defaultValue: 'Verification failed. Please try again.' }));
        return;
      }
    }

    setIsSending(true);
    try {
      const submissions = selectedExperts.map(expert => ({
        recipient_id: expert.id,
        sender_email: senderEmail,
        sender_name: senderName,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        service_type: formData.service_type || null,
        location: formData.location?.trim() || null,
        budget_range: formData.budget_range || null,
        timeline: formData.timeline || null,
        user_status: 'seeking_more',
        status: 'released_to_provider',
        source_type: 'form',
        email_verified: !isGuest, // Guest submissions are unverified
      }));

      const { data: insertedOpportunities, error } = await supabase
        .from('opportunities')
        .insert(submissions)
        .select('id, recipient_id');

      if (error) throw error;

      // Track analytics
      if (insertedOpportunities) {
        for (const opp of insertedOpportunities) {
          try {
            await supabase.functions.invoke('track-analytics-event', {
              body: {
                eventType: 'opportunity_sent',
                eventCategory: 'opportunity',
                targetUserId: opp.recipient_id,
                targetContentId: opp.id,
                sourcePage: window.location.pathname,
                metadata: {
                  serviceType: formData.service_type,
                  timeline: formData.timeline,
                  budgetRange: formData.budget_range,
                  expertCount: selectedExperts.length,
                  isGuest,
                  timestamp: new Date().toISOString()
                }
              }
            });
          } catch {
            // Silent fail
          }
        }
      }

      // Delete draft on successful send (only for logged-in users)
      if (currentDraftId && !isGuest) {
        await db.delete('opportunities_message_drafts', [{ column: 'id', operator: 'eq', value: currentDraftId }]);
      }

      toast.success(lCommon('toast.message_sent.success', { count: selectedExperts.length, defaultValue: 'Message sent to {{count}} expert(s).' }), { id: 'send-message' });

      // Invalidate outbox count queries so UI updates immediately
      queryClient.invalidateQueries({ queryKey: ['outbox-count'] });
      queryClient.invalidateQueries({ queryKey: ['user-opportunities'] });

      const sentExpertCountry = selectedExperts[0]?.country;
      setSentExperts([...selectedExperts]);
      setSubmissionState('success');

      await fetchSimilarExperts(selectedExperts.map(e => e.id), sentExpertCountry);

      onSuccess();
    } catch {
      toast.error(lCommon('toast.failed_to_send.message', { defaultValue: 'Failed to send message.' }), { id: 'send-message' });
    } finally {
      setIsSending(false);
    }
  };

  // Handle guest sign-up redirect - uses smart auth detection
  const handleGuestSignUp = useCallback(() => {
    // Store guest email for pre-filling signup form
    sessionStorage.setItem('pendingSignUpEmail', guestEmail);
    onOpenChange(false);
    navigate(getSmartAuthRoute());
  }, [guestEmail, navigate, onOpenChange]);

  const handleDialogClose = useCallback((shouldClose: boolean) => {
    if (!shouldClose) {
      // Only check for user-entered content (i.e. values that differ from
      // their pre-filled / auto-populated initial state). Auto-prefilled
      // location from UserContext or initialSubject/initialMessage props
      // should NOT trigger the discard-confirm prompt.
      const prefilledLocation = initialLocation || location || '';
      const prefilledSubject = initialSubject || '';
      const prefilledMessage = initialMessage || '';
      const prefilledServiceType = initialServiceType || '';

      const hasUserContent =
        (!lockedRecipients && selectedExperts.length > 0) ||
        (formData.subject.trim() !== '' && formData.subject !== prefilledSubject) ||
        (formData.message.trim() !== '' && formData.message !== prefilledMessage) ||
        (!!formData.service_type && formData.service_type !== prefilledServiceType) ||
        (formData.location.trim() !== '' && formData.location !== prefilledLocation) ||
        !!formData.budget_range ||
        !!formData.timeline;

      if (hasUserContent) {
        setShowDismissConfirm(true);
      } else {
        resetForm();
        onOpenChange(false);
      }
    }
  }, [lockedRecipients, selectedExperts.length, formData, onOpenChange, resetForm, initialLocation, location, initialSubject, initialMessage, initialServiceType]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDismissConfirm(false);
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  return (
    <>
      {!hideTrigger && (
        <Button onClick={() => onOpenChange(true)} className="gap-2 w-full">
          <Plus className="h-4 w-4" />
          <LComponents id="compose_message.button" defaultValue='Compose Message' />
        </Button>
      )}
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent 
          className={DIALOGS.xlargeScroll}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            {context === 'frontend' && selectedExperts.length > 0 ? (
              /* Frontend context: Email-style From/To layout */
              <>
                <DialogTitle className={DIALOG_TYPOGRAPHY.title}>
                  <LComponents 
                    id="compose_message.contact_expert" 
                    defaultValue="Contact Expert" 
                  />
                </DialogTitle>
                <DialogDescription>
                  <LComponents 
                    id="compose_message.frontend_description" 
                    defaultValue="Submit your inquiry to the selected expert." 
                  />
                </DialogDescription>
                
                {/* From/To section */}
                <div className="space-y-3 mt-4 pt-4 border-t border-border">
                  {/* FROM (Sender) - Guest: full-width card, Auth: inline with label */}
                  {isGuest ? (
                    <ComposeSenderInput
                      guestEmail={guestEmail}
                      guestName={guestName}
                      emailError={errors.guestEmail}
                      nameError={errors.guestName}
                      onEmailChange={setGuestEmail}
                      onNameChange={setGuestName}
                      onEmailBlur={() => handleFieldBlur('guestEmail', guestEmail)}
                      onNameBlur={() => handleFieldBlur('guestName', guestName)}
                      disabled={isSending}
                    />
                  ) : (
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-muted-foreground w-12 flex-shrink-0 pt-2">
                        <LCommon id="labels.from" defaultValue="From:" />
                      </span>
                      <ComposeSenderHeader 
                        userData={userData} 
                        avatarUrl={avatarUrl} 
                        displayName={senderDisplayName}
                        compact
                      />
                    </div>
                  )}
                  
                  {/* TO (Recipient) */}
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-muted-foreground w-12 flex-shrink-0 pt-2">
                      <LCommon id="labels.to" defaultValue="To:" />
                    </span>
                    <ComposeRecipientHeader 
                      recipients={selectedExperts}
                      compact
                    />
                  </div>
                </div>
                
                <Separator className="mt-4" />
              </>
            ) : (
              /* Account context: Show existing sender header + title */
              <>
                <div className={RESPONSIVE.flexRowBetweenGap4}>
                  <DialogTitle className={DIALOG_TYPOGRAPHY.title}>
                    <LComponents id="compose_message.title" defaultValue='Send Inquiry' />
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    <ComposeMessageHistory
                      drafts={drafts}
                      draftsLoading={draftsLoading}
                      onLoadDraft={handleLoadDraft}
                      onDeleteDraft={handleDeleteDraft}
                      onLoadSent={handleLoadSent}
                    />
                    {lastSavedAt && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Cloud className="h-3 w-3" />
                        {isDraftSaving ? lCommon('status.saving', { defaultValue: 'Saving' }) : lCommon('status.draft_saved', { defaultValue: 'Draft Saved' })}
                      </Badge>
                    )}
                  </div>
                </div>
                <DialogDescription className="mt-2">
                  <LComponents
                    id="compose_message.description"
                    defaultValue='Send a message to up to three experts to inquire about their services and availability.'
                  />
                </DialogDescription>
              </>
            )}
          </DialogHeader>

          {/* Sender Header - Only show for account context, not for guests */}
          {context !== 'frontend' && !isGuest && (
            <>
              <ComposeSenderHeader 
                userData={userData} 
                avatarUrl={avatarUrl} 
                displayName={senderDisplayName} 
              />
              <Separator />
            </>
          )}
          
          {/* Guest Sender Input - Show for account context guests */}
          {context !== 'frontend' && isGuest && (
            <>
              <ComposeSenderInput
                guestEmail={guestEmail}
                guestName={guestName}
                emailError={errors.guestEmail}
                nameError={errors.guestName}
                onEmailChange={setGuestEmail}
                onNameChange={setGuestName}
                onEmailBlur={() => handleFieldBlur('guestEmail', guestEmail)}
                onNameBlur={() => handleFieldBlur('guestName', guestName)}
                disabled={isSending}
              />
              <Separator />
            </>
          )}

          {submissionState === 'success' ? (
            <ComposeSuccessView
              sentExperts={sentExperts}
              similarExperts={similarExperts}
              selectedSimilarExperts={selectedSimilarExperts}
              onToggleSimilarExpert={handleToggleSimilarExpert}
              onSendToSimilar={handleSendToSimilar}
              onClose={handleCloseSuccess}
              isSendingBulk={isSendingBulk}
              isLoadingSimilarExperts={isLoadingSimilarExperts}
              isGuest={isGuest}
              guestEmail={guestEmail}
              onSignUp={handleGuestSignUp}
            />
          ) : (
            <div className={SPACING.dialogSection}>
              {/* Target Location + Recipients - Only show for account context */}
              {context !== 'frontend' && (
                <div className={cn(GRIDS.formGrid, 'md:grid-cols-4 items-end')}>
                  <div className="md:col-span-1">
                    <ComposeTargetLocation
                      formData={formData}
                      onFormChange={updateFormData}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <ComposeRecipientSection
                      selectedExperts={selectedExperts}
                      onAddExpert={handleAddExpert}
                      onRemoveExpert={handleRemoveExpert}
                      recentContacts={recentContacts}
                      locationMatches={locationMatches}
                      locationMatchesMode={locationMatchesMode}
                      experts={experts}
                      expertsLoading={expertsLoading}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      lockedRecipients={lockedRecipients}
                      context={context}
                      recipientError={errors.recipients}
                    />
                  </div>
                </div>
              )}

              {/* Message Form */}
              <ComposeMessageForm
                formData={formData}
                errors={errors}
                fieldStatus={fieldStatus}
                onFormChange={updateFormData}
                onFieldBlur={handleFieldBlur}
                selectedExperts={selectedExperts}
              />

              {/* Optional Details */}
              <ComposeOptionalDetails
                formData={formData}
                onFormChange={updateFormData}
              />
            </div>
          )}

          {/* Footer */}
          {submissionState === 'form' && (
            <div className="space-y-2">
              <div className={RESPONSIVE.dialogFooterEndGap3}>
                <Button variant="outline" onClick={() => handleDialogClose(false)} className="w-full sm:w-auto">
                  <LCommon id="actions.close" defaultValue='Close' />
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={isSending || selectedExperts.length === 0}
                  className="w-full sm:w-auto"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <LCommon id="status.sending" defaultValue='Sending' />
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      <LComponents
                        id="compose_message.send.button"
                        values={{ count: selectedExperts.length }}
                        defaultValue='Send'
                      />
                    </>
                  )}
                </Button>
              </div>
              {/* reCAPTCHA attribution for guests (required by Google TOS when hiding badge) */}
              {isGuest && recaptchaConfigured && (
                <p className="text-[10px] text-muted-foreground/60 text-center">
                  Protected by reCAPTCHA · {' '}
                  <button type="button" onClick={() => setPrivacyModalOpen(true)} className="underline hover:text-muted-foreground">
                    <LComponents id="compose_message.legal.privacy" defaultValue="Privacy" />
                  </button> · {' '}
                  <button type="button" onClick={() => setTermsModalOpen(true)} className="underline hover:text-muted-foreground">
                    <LComponents id="compose_message.legal.terms" defaultValue="Terms" />
                  </button>
                </p>
              )}
              <LegalDocumentModal slug="privacy-policy" open={privacyModalOpen} onOpenChange={setPrivacyModalOpen} />
              <LegalDocumentModal slug="terms-of-use" open={termsModalOpen} onOpenChange={setTermsModalOpen} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dismiss Confirmation Dialog */}
      <AlertDialog open={showDismissConfirm} onOpenChange={setShowDismissConfirm}>
        <AlertDialogContent className="z-50">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <LComponents id="compose_message.discard_dialog.title" defaultValue='Discard Draft' />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <LComponents
                id="compose_message.discard_dialog.description"
                defaultValue='Discard draft message.'
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={RESPONSIVE.alertDialogFooter}>
            <AlertDialogCancel className="w-full sm:w-auto">
              <LCommon id="actions.keep" defaultValue='Keep' />
            </AlertDialogCancel>
            <AlertDialogAction className="w-full sm:w-auto" onClick={handleConfirmDiscard}>
              <LCommon id="actions.discard" defaultValue='Discard' />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
